// src/utils/consentService.js
// Parental consent for users under 18.
//   users/{uid}                  -> { isMinor, parentUid, parentUsername, consentStatus,
//                                     consentRequestedAt, consentDecidedAt, ... }
//   users/{uid}/private/profile  -> { dob, createdAt }   (owner-only, written once)
// consentStatus: "pending" | "approved" | "denied" | "revoked"

import {
  getFirestore,
  doc,
  getDoc,
  updateDoc,
  writeBatch,
  collection,
  query,
  where,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { normalize } from "./usernameService";

export const MIN_AGE = 13;
export const ADULT_AGE = 18;
export const RETRY_AFTER_MS = 24 * 60 * 60 * 1000; // wait after a denial/revoke

export class ConsentError extends Error {
  constructor(code, message) {
    super(message);
    this.code = code;
  }
}

/* ---------- Date of birth ---------- */

// dob is "YYYY-MM-DD" (what <input type="date"> gives). Returns null if invalid.
function parseDob(dob) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dob || "");
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) {
    return null;
  }
  return { y, mo, d };
}

/** Age in years, or null for an invalid, future or impossible date. */
export function calcAge(dob) {
  const p = parseDob(dob);
  if (!p) return null;
  const now = new Date();
  const month = now.getMonth() + 1;
  let age = now.getFullYear() - p.y;
  if (month < p.mo || (month === p.mo && now.getDate() < p.d)) age -= 1;
  return age >= 0 && age <= 120 ? age : null;
}

/* ---------- Parent lookup ---------- */

/** Finds the parent by username. Throws ConsentError with a clear message. */
export async function findParent(parentUsername, ownUid) {
  const db = getFirestore();
  const name = normalize(String(parentUsername || "").replace(/^@/, ""));
  if (!name) {
    throw new ConsentError("parent-missing", "Enter your parent’s username.");
  }

  const nameSnap = await getDoc(doc(db, "usernames", name));
  if (!nameSnap.exists()) {
    throw new ConsentError("parent-not-found", `We couldn’t find @${name}.`);
  }

  const parentUid = nameSnap.data().uid;
  if (parentUid === ownUid) {
    throw new ConsentError(
      "parent-self",
      "Enter your parent’s username, not your own."
    );
  }

  const parentSnap = await getDoc(doc(db, "users", parentUid));
  const p = parentSnap.exists() ? parentSnap.data() : {};
  if (p.isMinor === true) {
    throw new ConsentError(
      "parent-not-adult",
      "Your parent or guardian must be 18 or older."
    );
  }
  if (p.isMinor !== false) {
    throw new ConsentError(
      "parent-not-ready",
      `@${name} needs to open Trescend and confirm their age first.`
    );
  }
  return { parentUid, parentUsername: name };
}

/* ---------- Teen side ---------- */

/**
 * First-time age step (everyone). One atomic batch:
 *  - private/profile gets the DOB (write-once)
 *  - users/{uid} gets isMinor (+ the parent request for 13 to 17)
 * Under 13 is rejected here and nothing is saved.
 */
export async function saveAge({ uid, dob, parentUsername }) {
  const age = calcAge(dob);
  if (age === null) {
    throw new ConsentError("bad-dob", "Enter a valid date of birth.");
  }
  if (age < MIN_AGE) {
    throw new ConsentError("under13", "You must be 13 or older to use Trescend.");
  }

  const isMinor = age < ADULT_AGE;
  const patch = { isMinor };

  if (isMinor) {
    const parent = await findParent(parentUsername, uid);
    patch.parentUid = parent.parentUid;
    patch.parentUsername = parent.parentUsername;
    patch.consentStatus = "pending";
    patch.consentRequestedAt = serverTimestamp();
  }

  const db = getFirestore();
  const batch = writeBatch(db);
  batch.set(doc(db, "users", uid, "private", "profile"), {
    dob,
    createdAt: serverTimestamp(),
  });
  batch.set(doc(db, "users", uid), patch, { merge: true });
  await batch.commit();

  return { isMinor };
}

/**
 * Ask a parent (again), or switch to a different parent.
 * After a denial/revoke the teen waits RETRY_AFTER_MS before asking again.
 */
export async function requestParentConsent({
  uid,
  parentUsername,
  status,
  decidedAtMs,
}) {
  if ((status === "denied" || status === "revoked") && decidedAtMs) {
    const left = decidedAtMs + RETRY_AFTER_MS - Date.now();
    if (left > 0) {
      const hours = Math.ceil(left / 3600000);
      throw new ConsentError(
        "cooldown",
        `You can ask again in ${hours} hour${hours === 1 ? "" : "s"}.`
      );
    }
  }

  const parent = await findParent(parentUsername, uid);
  await updateDoc(doc(getFirestore(), "users", uid), {
    parentUid: parent.parentUid,
    parentUsername: parent.parentUsername,
    consentStatus: "pending",
    consentRequestedAt: serverTimestamp(),
  });
}

/* ---------- Parent side ---------- */

/** Approve, deny or revoke. Firestore rules allow only these two fields. */
export async function setConsentStatus(teenUid, status) {
  if (!["approved", "denied", "revoked"].includes(status)) {
    throw new ConsentError("invalid", "Invalid status.");
  }
  await updateDoc(doc(getFirestore(), "users", teenUid), {
    consentStatus: status,
    consentDecidedAt: serverTimestamp(),
  });
}

/** Live list of teens who named this user as their parent. Returns unsubscribe. */
export function listenToChildren(parentUid, onData, onError) {
  const q = query(
    collection(getFirestore(), "users"),
    where("parentUid", "==", parentUid)
  );
  return onSnapshot(
    q,
    (snap) => {
      const order = { pending: 0, approved: 1, denied: 2, revoked: 2 };
      const rows = snap.docs
        .map((d) => {
          const x = d.data();
          return {
            uid: d.id,
            username: x.username || "user",
            status: x.consentStatus || "pending",
            requestedAtMs: x.consentRequestedAt?.toMillis?.() ?? 0,
          };
        })
        .sort(
          (a, b) =>
            (order[a.status] ?? 3) - (order[b.status] ?? 3) ||
            b.requestedAtMs - a.requestedAtMs
        );
      onData(rows);
    },
    onError
  );
}

/* ---------- localStorage flags (0 reads once set) ---------- */

const doneKey = (uid) => `consent_done_${uid}`;
const blockKey = (uid) => `consent_under13_${uid}`;

const getFlag = (key) => {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};
const setFlag = (key) => {
  try {
    localStorage.setItem(key, "1");
  } catch {
    /* ignore */
  }
};

// Adults only: lets the gate skip all reads on later visits.
export const isConsentDone = (uid) => getFlag(doneKey(uid));
export const setConsentDone = (uid) => setFlag(doneKey(uid));

// Under 13: keeps the blocked screen up on this browser.
export const isUnder13Blocked = (uid) => getFlag(blockKey(uid));
export const setUnder13Blocked = (uid) => setFlag(blockKey(uid));